import KeyboardDoubleArrowLeftIcon from "@mui/icons-material/KeyboardDoubleArrowLeft";
import KeyboardDoubleArrowRightIcon from "@mui/icons-material/KeyboardDoubleArrowRight";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Skeleton,
  Stack,
} from "@mui/material";
import * as React from "react";
import { useTranslation } from "react-i18next";
import { EmptyState } from "../../../components/EmptyState";
import { LocationNavigationHeader } from "../../../components/LocationNavigationHeader";
import { TimeControlBar } from "../../../components/TimeControlBar";
import { PredefinedObstacle } from "../../../hooks/usePredefinedObstacle";
import { Hangar } from "../../../types";
import { MovableObstacleGrid } from "../../../widgets/AddMovableObstacle/MovableObstacleGrid";
import { HangarStack } from "../../Hangar/Hangar";
import { HangarPresenterBase } from "../../Hangar/HangarPresenterBase";

interface ScheduledHangarViewPresenterProps {
  // Core props
  id: string;
  stacks: HangarStack[];
  setStacks: (stacks: HangarStack[]) => void;
  isReference?: boolean;
  isAlgorithmTesting?: boolean;

  // State data
  baseHangar: Hangar | null;
  scheduledHangar: Hangar | null;
  currentTimestamp: Date;
  initialLoading: boolean;
  isUpdating: boolean;
  isPending: boolean;
  hasLoadedOnce: boolean;
  hasNextTrip: boolean;
  hasPreviousTrip: boolean;
  showAddObstacleDialog: boolean;
  isSchedulingEnabled: boolean;

  // Drawer state
  isDrawerOpen: boolean;
  setIsDrawerOpen: (open: boolean) => void;

  // Data
  tenantTripMap: Map<string, string>;
  placementsInRange: any[];
  tripCountsByDate: Map<string, number>;
  predefinedObstacles: PredefinedObstacle[];
  originalStackId: string | null;
  addedObstacles: any[];
  timeRange: { start: Date; end: Date };
  selectedTripInfo: {
    start: Date;
    end: Date;
    tailNumber: string;
  } | null;

  // Callbacks
  handleTimeChange: (newTime: Date) => void;
  handleJumpToNextTrip: () => Promise<void>;
  handleJumpToPreviousTrip: () => Promise<void>;
  handleHangarChange: (updatedHangar: Hangar) => void;
  handleUndo: () => void;
  handleAircraftRemoval: (tenantId: string) => void;
  setShowAddObstacleDialog: (show: boolean) => void;
  setRefreshKey: React.Dispatch<React.SetStateAction<number>>;
  setAddedObstaclesWithLogging: (obstacles: any[]) => void;

  // Services
  placementService: any;

  // Feature flags
  showGranularityToggle: boolean;
}

export const ScheduledHangarViewPresenter: React.FC<
  ScheduledHangarViewPresenterProps
> = ({
  id,
  stacks,
  setStacks,
  isReference,
  isAlgorithmTesting,
  baseHangar,
  scheduledHangar,
  currentTimestamp,
  initialLoading,
  isUpdating,
  isPending,
  hasLoadedOnce,
  hasNextTrip,
  hasPreviousTrip,
  showAddObstacleDialog,
  isSchedulingEnabled,
  isDrawerOpen,
  setIsDrawerOpen,
  tenantTripMap,
  placementsInRange,
  tripCountsByDate,
  predefinedObstacles,
  originalStackId,
  addedObstacles,
  timeRange,
  selectedTripInfo,
  handleTimeChange,
  handleJumpToNextTrip,
  handleJumpToPreviousTrip,
  handleHangarChange,
  handleUndo,
  handleAircraftRemoval,
  setShowAddObstacleDialog,
  setRefreshKey,
  setAddedObstaclesWithLogging,
  placementService,
  showGranularityToggle,
}) => {
  const { t } = useTranslation();

  return (
    <Stack direction="column" sx={{ height: "100vh" }}>
      {/* Navigation and time controls in unified header */}
      <Stack direction="row">
        {/* Left side: Navigation that will be unified with info card below */}
        <Box
          sx={{
            width: isDrawerOpen ? "400px" : "125px", // Match sidebar width
            minWidth: isDrawerOpen ? "400px" : "125px", // Prevent flex shrinking
            flexShrink: 0, // Disable flexbox shrinking
            borderRight: 1,
            borderColor: "divider",
            p: isDrawerOpen ? 2 : 1, // Less padding when collapsed
          }}
        >
          {isDrawerOpen ? (
            <LocationNavigationHeader
              locationName={baseHangar?.name || ""}
              locationId={id}
              locationType="hangar"
              isSchedulingEnabled={isSchedulingEnabled}
              rightActions={
                <IconButton onClick={() => setIsDrawerOpen(false)}>
                  <KeyboardDoubleArrowLeftIcon />
                </IconButton>
              }
            />
          ) : (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <IconButton onClick={() => setIsDrawerOpen(true)}>
                <KeyboardDoubleArrowRightIcon />
              </IconButton>
            </Box>
          )}
        </Box>

        {/* Right side: Time controls */}
        <Box
          sx={{
            flexGrow: 1,
            pt: 2,
            pb: 3,
          }}
        >
          <TimeControlBar
            currentTime={currentTimestamp}
            onTimeChange={handleTimeChange}
            tripCountsByDate={tripCountsByDate}
            selectedTripStart={selectedTripInfo?.start}
            selectedTripEnd={selectedTripInfo?.end}
            showTripDuration={!!selectedTripInfo}
            selectedTailNumber={selectedTripInfo?.tailNumber}
            onJumpToNextTrip={handleJumpToNextTrip}
            onJumpToPreviousTrip={handleJumpToPreviousTrip}
            showTripJumpButtons={true}
            hasNextTrip={hasNextTrip}
            hasPreviousTrip={hasPreviousTrip}
            showGranularityToggle={showGranularityToggle}
          />
        </Box>
      </Stack>

      {/* Subtle loading indicator for background updates - only for non-date changes */}
      {isUpdating && (
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            gap: 1,
            backgroundColor: "background.paper",
            borderRadius: 1,
            px: 1.5,
            py: 0.5,
            boxShadow: 1,
          }}
        >
          <CircularProgress size={16} />
          <Box sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
            {t('screens.schedule.scheduledHangarView.updating')}
          </Box>
        </Box>
      )}

      {/* Hangar presenter with scheduled data */}
      <Stack
        sx={{
          flex: 1,
          overflow: "hidden",
          transition: "opacity 0.2s ease-in-out",
          opacity: isPending ? 0.8 : 1,
        }}
      >
        {initialLoading ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              p: 4,
            }}
          >
            <Skeleton variant="text" width="60%" height={40} sx={{ mb: 2 }} />
            <Skeleton
              variant="rectangular"
              width="90%"
              height={400}
              sx={{ mb: 2 }}
            />
            <Stack direction="row" spacing={2} width="90%">
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
              <Skeleton variant="rectangular" width="30%" height={100} />
            </Stack>
          </Box>
        ) : scheduledHangar ? (
          <HangarPresenterBase
            id={id}
            stacks={stacks}
            setStacks={setStacks}
            isReference={isReference}
            isAlgorithmTesting={isAlgorithmTesting}
            hangar={scheduledHangar}
            setHangar={handleHangarChange}
            history={{ undo: () => {} }} // Empty history for scheduled view
            isScheduledView={true}
            onUndo={handleUndo}
            scheduledViewProps={{
              tenantTripMap,
              placementService,
              currentTime: currentTimestamp,
              onScheduledUpdate: () => {
                // Trigger a refresh after a short delay to ensure DB has updated
                setTimeout(() => {
                  setRefreshKey((prev) => prev + 1);
                }, 300);
              },
              timeRange: timeRange,
              allPlacements: placementsInRange,
              onRemoveAircraft: handleAircraftRemoval,
              onTimeChange: handleTimeChange,
            }}
            onScheduledUpdate={() => {
              // Trigger a refresh after a short delay to ensure DB has updated
              setTimeout(() => {
                setRefreshKey((prev) => prev + 1);
              }, 300);
            }}
          />
        ) : hasLoadedOnce ? (
          <EmptyState
            title={t('screens.schedule.scheduledHangarView.errors.noData.title')}
            description={t('screens.schedule.scheduledHangarView.errors.noData.description')}
            actionLabel={t('screens.schedule.scheduledHangarView.errors.noData.actionLabel')}
            onAction={() => setRefreshKey((prev) => prev + 1)}
          />
        ) : null}
      </Stack>

      {/* Add Obstacle Dialog */}
      <Dialog
        open={showAddObstacleDialog}
        onClose={() => setShowAddObstacleDialog(false)}
        maxWidth="lg"
      >
        <DialogTitle>{t('screens.schedule.scheduledHangarView.addObstacleDialog.title')}</DialogTitle>
        <DialogContent sx={{ width: 900, height: 600 }}>
          {baseHangar && (
            <MovableObstacleGrid
              location={baseHangar}
              movableObstacles={predefinedObstacles}
              stack={{
                ...baseHangar.stack,
                id: originalStackId!, // Use preserved real stack ID for database operations
              }}
              setStack={(newStack) => {
                const baseObstacles = baseHangar.stack.movableObstacles || [];
                const currentAdded = addedObstacles;

                // Handle additions (removals are handled in handleHangarChange)
                const updatedAdded = [];
                const newlyAdded = [];

                for (const obstacle of newStack.movableObstacles) {
                  const isFromBase = baseObstacles.find(
                    (base) => base.id === obstacle.id
                  );
                  const isFromAdded = currentAdded.find(
                    (added) => added.id === obstacle.id
                  );

                  if (isFromBase) {
                    // Base obstacle - position changes handled elsewhere, skip
                    continue;
                  } else if (isFromAdded) {
                    // Existing added obstacle - update position
                    updatedAdded.push(obstacle);
                  } else {
                    // Truly new obstacle
                    newlyAdded.push(obstacle);
                  }
                }

                // Update addedObstacles with position changes + new obstacles
                const finalObstacles = [...updatedAdded, ...newlyAdded];
                setAddedObstaclesWithLogging(finalObstacles);
              }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddObstacleDialog(false)}>
            {t('screens.schedule.scheduledHangarView.addObstacleDialog.cancelButton')}
          </Button>
          <Button
            variant="contained"
            color="success"
            onClick={() => {
              setShowAddObstacleDialog(false);
              // Don't clear addedObstacles - they should persist in scheduled view
              // The obstacles are saved to database but should remain visible
              // Trigger refresh to reload from database
              setRefreshKey((prev) => prev + 1);
            }}
          >
            {t('screens.schedule.scheduledHangarView.addObstacleDialog.doneButton')}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
};
